
import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const parseTimeoutEnv = (rawValue, fallback) => {
  const parsed = Number.parseInt(String(rawValue ?? ''), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const AUTH_CRITICAL_TIMEOUT_MS = parseTimeoutEnv(process.env.REACT_APP_API_CRITICAL_TIMEOUT_MS, 60000);
const LOGIN_REQUEST_TIMEOUT_MS = parseTimeoutEnv(process.env.REACT_APP_LOGIN_TIMEOUT_MS, Math.max(120000, AUTH_CRITICAL_TIMEOUT_MS));
const VERIFY_REQUEST_TIMEOUT_MS = parseTimeoutEnv(process.env.REACT_APP_AUTH_VERIFY_TIMEOUT_MS, AUTH_CRITICAL_TIMEOUT_MS);

const resolveLoginErrorMessage = (error) => {
  const backendMessage = String(error?.response?.data?.message || '').trim();
  if (backendMessage) return backendMessage;

  const status = Number(error?.response?.status || 0);
  if (status === 401) return 'Invalid email or password.';
  if (status === 429) return 'Too many sign-in attempts. Please wait and try again.';
  if (error?.code === 'ECONNABORTED') return 'Sign-in timed out. Please try again.';
  if (!error?.response) return 'Unable to reach the server. Check your connection and try again.';
  return 'Unable to sign in right now. Please try again.';
};


// Always attach token from localStorage for every request
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      delete config.headers['Authorization'];
    }
    return config;
  },
  error => Promise.reject(error)
);

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const lastVerifyRef = React.useRef(0);

  const logout = () => {
    const activeToken = localStorage.getItem('token');

    // Clear client state immediately so the UI logs out without waiting on the
    // network. The token itself stays in storage until the invalidation request
    // settles, because the request interceptor reads it from storage.
    setUser(null);
    setToken(null);
    lastVerifyRef.current = 0;
    delete api.defaults.headers.common['Authorization'];

    const clearStoredToken = () => {
      localStorage.removeItem('token');
    };

    if (!activeToken) {
      clearStoredToken();
      return;
    }

    // Best-effort server-side invalidation. The backend rotates `last_login`,
    // which makes every token issued for the previous session stale (including
    // this one), so the access token cannot be replayed after logout.
    //
    // The body MUST be an object: the shared client sends
    // `Content-Type: application/json`, so axios serialises a `null` payload to
    // the literal string `null`, which the server's strict JSON body parser
    // rejected with 400 INVALID_JSON before the route ever ran.
    api.post('/auth/logout', {}, {
      meta: { suppressToast: true, skipAuthRedirect: true }
    })
      .catch(() => {
        // Logout must always succeed locally, even if the server call fails.
      })
      .finally(clearStoredToken);
  };

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await api.get('/auth/verify', {
          timeout: VERIFY_REQUEST_TIMEOUT_MS
        });
        setUser(response.data.data.user);
        lastVerifyRef.current = Date.now();
      } catch (error) {
        console.error('Token verification failed:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Only verify if we haven't verified in the last 5 minutes
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      if (now - lastVerifyRef.current > fiveMinutes) {
        verifyToken();
      } else {
        // Reuse cached user state, just mark as loaded
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [token]);

const login = async (identifier, password) => {
  try {
    const response = await api.post('/auth/login', { identifier, password }, {
      timeout: LOGIN_REQUEST_TIMEOUT_MS,
      meta: {
        suppressToast: true,
        skipAuthRedirect: true
      }
    });
    // Try different possible response structures
    let newToken, userData;
    
    if (response.data.data) {
      // Expected structure: { success: true, data: { access_token: '...', user: {...} } }
      newToken = response.data.data.access_token;
      userData = response.data.data.user;
    } else if (response.data.access_token) {
      // Alternative structure: { access_token: '...', user: {...} }
      newToken = response.data.access_token;
      userData = response.data.user;
    } else if (response.data.token) {
      // Another alternative: { token: '...', user: {...} }
      newToken = response.data.token;
      userData = response.data.user;
    }
    
    
    if (!newToken) {
      throw new Error('No token received from server');
    }
    
    // Set token first, then user - this ensures isAuthenticated becomes true immediately
    localStorage.setItem('token', newToken);
    
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    
    setToken(newToken);
    setUser(userData);

    try {
      const verifyRes = await api.get('/auth/verify', {
        timeout: VERIFY_REQUEST_TIMEOUT_MS
      });
      if (verifyRes?.data?.data?.user) {
        setUser(verifyRes.data.data.user);
      }
      lastVerifyRef.current = Date.now();
    } catch (_) {
      // Keep login response user if verify call fails.
    }

    setLoading(false);
    return { success: true };
  } catch (error) {
    setLoading(false);
    console.error('[AuthContext] login() error response:', error.response);
    return {
      success: false,
      error: resolveLoginErrorMessage(error)
    };
  }
};  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    isAuthenticated: !!token && !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
